import React from 'react';
import { useQuery } from 'react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { api } from '../services/api';

const STAGE_LABELS = {
  stt: 'Speech to Text',
  translation: 'Translation',
  tts: 'Text to Speech',
};

const formatUsd = (value) => {
  const numericValue = Number(value || 0);
  return `$${numericValue.toFixed(numericValue > 0 && numericValue < 0.01 ? 6 : 4)}`;
};

const formatNumber = (value) => Number(value || 0).toLocaleString('en-US');

const formatStageQuantity = (stage, item = {}) => {
  if (stage === 'stt' && item.billingUnit === 'audio_ms') {
    return `${(Number(item.quantity || 0) / 60000).toFixed(2)} minutes`;
  }
  return `${formatNumber(item.quantity)} characters`;
};

const formatDateTime = (value) => (
  value ? new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value)) : '-'
);

const formatDurationMs = (value) => {
  const duration = Number(value);
  if (!Number.isFinite(duration) || duration < 0) return '-';
  const totalSeconds = Math.round(duration / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes > 0) {
    return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
  }
  return `${seconds}s`;
};

export default function CostSummary() {
  const {
    data: summary,
    error,
    isLoading,
    isFetching,
    refetch,
  } = useQuery('translationCostSummary', () => api.getTranslationCostSummary(), {
    refetchInterval: 30000,
  });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  const stages = summary?.stages || {};
  const recentSessions = summary?.recentSessions || [];

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5">AI2 Provider Costs</Typography>
          <Typography variant="body2" color="text.secondary">
            Current calendar-month provider API estimates before free tiers and billing credits. Local infrastructure is excluded.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => refetch()}
          disabled={isFetching}
          sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
        >
          Refresh
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Could not load AI2 provider costs: {error.message}
        </Alert>
      )}

      {summary?.unknownEstimateRecords > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {formatNumber(summary.unknownEstimateRecords)} provider usage record(s) have no configured cost estimate.
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 3 }}>
        This ledger starts from deployment time and records live AI2 provider usage only. SIP Simulator fixture-mode
        validation tones do not call providers and do not create billable ledger rows.
      </Alert>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Estimated cost</Typography>
              <Typography variant="h5">{formatUsd(summary?.estimatedCostUsd)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">AI2 sessions</Typography>
              <Typography variant="h5">{formatNumber(summary?.sessionCount)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Provider usage records</Typography>
              <Typography variant="h5">{formatNumber(summary?.usageRecords)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">TTS cache hits</Typography>
              <Typography variant="h5">{formatNumber(summary?.cacheHits)}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Provider Stage Breakdown</Typography>
        <Grid container spacing={2}>
          {['stt', 'translation', 'tts'].map((stage) => {
            const item = stages[stage] || {};
            return (
              <Grid item xs={12} md={4} key={stage}>
                <Box>
                  <Typography variant="subtitle2">{STAGE_LABELS[stage]}</Typography>
                  <Typography variant="h6">{formatUsd(item.estimatedCostUsd)}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatStageQuantity(stage, item)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatNumber(item.usageRecords)} records
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6">Recent Transactions</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Privacy-safe per-session usage estimates. Transcript text is not stored in this ledger.
        </Typography>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Started</TableCell>
                <TableCell>Call ID</TableCell>
                <TableCell>License</TableCell>
                <TableCell>Call duration</TableCell>
                <TableCell align="right">STT minutes</TableCell>
                <TableCell align="right">STT cost</TableCell>
                <TableCell align="right">Translation chars</TableCell>
                <TableCell align="right">Translation cost</TableCell>
                <TableCell align="right">TTS chars</TableCell>
                <TableCell align="right">TTS cost</TableCell>
                <TableCell align="right">Total cost</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recentSessions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    No live AI2 provider transactions have been recorded this month.
                  </TableCell>
                </TableRow>
              )}
              {recentSessions.map((session) => (
                <TableRow key={session.sessionId}>
                  <TableCell>{formatDateTime(session.startedAt)}</TableCell>
                  <TableCell>{session.callId}</TableCell>
                  <TableCell>{session.licenseLevel || 'UNKNOWN'}</TableCell>
                  <TableCell>{formatDurationMs(session.callDurationMs)}</TableCell>
                  <TableCell align="right">{(Number(session.sttAudioMs || 0) / 60000).toFixed(2)}</TableCell>
                  <TableCell align="right">{formatUsd(session.sttEstimatedCostUsd)}</TableCell>
                  <TableCell align="right">{formatNumber(session.translationCharacters)}</TableCell>
                  <TableCell align="right">{formatUsd(session.translationEstimatedCostUsd)}</TableCell>
                  <TableCell align="right">{formatNumber(session.ttsCharacters)}</TableCell>
                  <TableCell align="right">{formatUsd(session.ttsEstimatedCostUsd)}</TableCell>
                  <TableCell align="right">{formatUsd(session.estimatedCostUsd)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Paper>
    </Box>
  );
}
